import cn from "classnames";
import React from "react";
import BranchLink from "web/react/components/branch-link/branch-link";
import Button from "web/react/components/button/button";
import { socialLogin } from "web/react/components/email-capture-overlay/email-capture-overlay";
import GenericStickyModal from "web/react/components/generic-sticky-modal/generic-sticky-modal";
import PolicyText from "web/react/components/policy-text/policy-text";
import SignInLink from "web/react/components/sign-in-link/sign-in-link";
import SocialButton from "web/react/components/social-button/social-button";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import globals from "web/script/modules/globals";
import { RawBranchLink } from "web/script/utils/branch-io";
import history from "web/script/utils/history";
import { CustomerCaptureOverlaySerializer, ProductPreviewSerializer } from "web/types/serializers";
import styles from "./product-preview-sign-up.css";

export const PRODUCT_PREVIEW_SIGN_UP = "product-preview-sign-up";

type ProductPreviewSignUpProps = {
    customerCaptureData: CustomerCaptureOverlaySerializer;
};

export default function ProductPreviewSignUp({
    customerCaptureData,
}: ProductPreviewSignUpProps): JSX.Element | null {
    const { privacyPolicyLink, termsAndConditionsLink, signInLink, registerLink, requiresOptIn } =
        customerCaptureData;

    const overlay = useOverlayWatcher(PRODUCT_PREVIEW_SIGN_UP);
    const isOpen = !!overlay;

    const { data: preview, isFetching } = useRothko<ProductPreviewSerializer>(
        "modules/product/product_preview",
        { link_id: overlay && overlay.linkId },
        { enabled: isOpen }
    );

    function onClose(): void {
        history.popOverlay(PRODUCT_PREVIEW_SIGN_UP);
    }

    function onSocialClick(event, authProvider): void {
        event.preventDefault();

        // signup_facebook, signup_google
        analytics.event("product_preview", "click_social_sign_up", `signup_${authProvider}`);

        socialLogin({
            authProvider,
            next: preview?.product_link,
            // Currently, PDP Preview experiment is rolled out only for US.
            // Therefore it's not required the User to opt-in and marketingEmails are enabled by default.
            // But if we roll out the experiment for other countries, we need to handle opt-in process,
            // i.e. making sure that the User checked the opt-in checkbox.
            marketingEmails: !requiresOptIn,
            productId: preview?.id,
            designerId: preview?.designer_id,
            type: "save_for_later",
            searchTerm: null,
            searchGender: null,
        });
    }

    if (isFetching) {
        return null;
    }

    const image = preview?.images[0];
    const appDeepLinkURL = new URL(preview?.product_link || "", globals.window.location.href);

    return (
        <GenericStickyModal
            isOpen={isOpen}
            onClose={onClose}
            className={styles.modal}
            styleType={"fullscreen"}
        >
            <div className={styles.modalContent} data-allowTouch={true}>
                <h3 className={cn(styles.heading, "mb30")}>{gettext("app_landing.title")}</h3>

                {image && (
                    <div className={styles.imageWrapper}>
                        <img src={image.full_size_url} alt={image.alt_text} />
                    </div>
                )}

                <BranchLink
                    primaryButton
                    className={styles.appButton}
                    branchLink={RawBranchLink.AppSignupS4L}
                    title={gettext("account.register.continue_in_app")}
                    appDeepLinkURL={appDeepLinkURL}
                    showAppIcon
                />

                <div className={styles.separatorWrapper}>
                    <div className={styles.separatorLine} />
                    <div className={styles.separatorText}>
                        {gettext("in_stock_product.or_separator")}
                    </div>
                    <div className={styles.separatorLine} />
                </div>

                <Button
                    className={cn(styles.emailButton, "mb10")}
                    styleType="cta"
                    fullWidth
                    href={registerLink}
                >
                    {gettext("account.register.sign_up_with_email")}
                </Button>

                <SocialButton
                    className={"mb10"}
                    type="facebook"
                    onClick={(event) => onSocialClick(event, "facebook")}
                    fullWidth
                >
                    {gettext("account.register.sign_up_with_facebook")}
                </SocialButton>
                <SocialButton
                    className={"mb10"}
                    type="google"
                    onClick={(event) => onSocialClick(event, "google-oauth2")}
                    fullWidth
                >
                    {gettext("account.register.sign_up_with_google")}
                </SocialButton>

                <SignInLink
                    className={styles.signInText}
                    signInLink={signInLink}
                    next={preview?.product_link}
                    type={"save_for_later"}
                    productId={preview?.id}
                    designerId={preview?.designer_id}
                    area={"heart"}
                />
                <PolicyText
                    className={styles.policyText}
                    privacyPolicyLink={privacyPolicyLink}
                    termsAndConditionsLink={termsAndConditionsLink}
                />
            </div>
        </GenericStickyModal>
    );
}
